import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useCan } from "@/lib/capabilities";
import VideoLibraryPanel, { VideoPlayerStrip, TranscriptTab } from "@/components/VideoLibrary";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import LiquidGlassEMRCourseView from "@/components/LiquidGlassEMRCourseView";
import GatedCourseView from "@/components/GatedCourseView";
import {
  ChevronRight, ChevronLeft, CheckCircle2, Clock, BookOpen,
  Rocket, Phone, Shield, Target, Presentation, BarChart3, Search,
  Calendar, Brain, DollarSign, Globe, TrendingUp, Heart,
  HeartPulse, Building2, Scale, Radio, Layers, Flag, Smartphone,
  Table2, Users, ArrowLeft, ArrowRight, Loader2, GraduationCap,
  Video, Sparkles, AlertTriangle, RefreshCw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ICON_MAP = {
  rocket: Rocket, phone: Phone, shield: Shield, target: Target,
  presentation: Presentation, chart: BarChart3, search: Search,
  calendar: Calendar, brain: Brain, metrics: TrendingUp,
  funnel: TrendingUp, dollar: DollarSign, globe: Globe,
  heart: Heart, heartpulse: HeartPulse, building: Building2,
  scale: Scale, broadcast: Radio, layers: Layers, flag: Flag,
  smartphone: Smartphone, table: Table2, users: Users,
};

const COURSE_GRADIENT = {
  blue: "linear-gradient(135deg, #0A1628 0%, #1B365D 50%, #0071E3 100%)",
  rose: "linear-gradient(135deg, #4C0519 0%, #9F1239 50%, #F43F5E 100%)",
  cyan: "linear-gradient(135deg, #062C33 0%, #155E75 50%, #06B6D4 100%)",
  emerald: "linear-gradient(135deg, #022C22 0%, #064E3B 50%, #10B981 100%)",
  amber: "linear-gradient(135deg, #451A03 0%, #78350F 50%, #F59E0B 100%)",
};

const COURSE_ACCENT = {
  blue: "#0071E3", rose: "#E11D48", cyan: "#06B6D4", emerald: "#10B981", amber: "#F59E0B",
};

const CHAPTER_ACCENTS = [
  "#0071E3", "#10B981", "#F59E0B", "#8B5CF6", "#E11D48",
  "#06B6D4", "#6366F1", "#F97316", "#14B8A6", "#EC4899",
  "#84CC16", "#0EA5E9", "#A855F7",
];

/* ─── Markdown Rendering ─── */
const mdComponents = {
  h3: ({ children }) => <h3 className="text-[13px] font-bold text-[#1B365D] mt-5 mb-2">{children}</h3>,
  p: ({ children }) => <p className="text-[13px] leading-relaxed text-slate-600 mb-3">{children}</p>,
  ul: ({ children }) => <ul className="space-y-1 mb-3 ml-4">{children}</ul>,
  ol: ({ children }) => <ol className="space-y-1 mb-3 ml-4 list-decimal">{children}</ol>,
  li: ({ children }) => <li className="text-[13px] text-slate-600 leading-relaxed list-disc">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1B365D]">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-500">{children}</em>,
  a: ({ href, children }) => <a href={href} className="text-[#0071E3] hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-[#0071E3] bg-blue-50/60 px-4 py-3 my-3 rounded-r-lg text-[13px] text-slate-700 italic">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3 rounded-lg border border-slate-200">
      <table className="w-full text-[12px] border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#1B365D] text-white">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-[11px]">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-slate-600 border-t border-slate-100">{children}</td>,
  tr: ({ children }) => <tr className="even:bg-slate-50/50">{children}</tr>,
  hr: () => <hr className="my-4 border-slate-200" />,
};

/* ─── Chapter Reader ─── */
function ChapterReader({ chapter, course, sectionIndex, onSectionChange, onBack, onComplete, onAutoComplete, completedSections, completedChapters, onSelectChapter }) {
  const sections = chapter.sections || [];
  const accent = COURSE_ACCENT[course.color] || COURSE_ACCENT.blue;
  const allChapters = course.chapters || [];
  const currentChIdx = allChapters.findIndex((c) => c.id === chapter.id);
  const prevChapter = currentChIdx > 0 ? allChapters[currentChIdx - 1] : null;
  const nextChapter = currentChIdx >= 0 && currentChIdx < allChapters.length - 1 ? allChapters[currentChIdx + 1] : null;
  const isChapterComplete = !!completedChapters?.[chapter.id];
  const completedCount = allChapters.filter((c) => completedChapters?.[c.id]).length;
  const progressPct = allChapters.length > 0 ? Math.round((completedCount / allChapters.length) * 100) : 0;

  // Tab state: notes | transcript | resources
  const [tab, setTab] = useState("notes");
  const [activeVideo, setActiveVideo] = useState(null);
  const [currentPos, setCurrentPos] = useState(0);
  // Reset transcript playhead + tab when chapter changes — but keep
  // "transcript" tab sticky within a chapter so navigating back into it
  // shows where the learner left off.
  useEffect(() => { setTab("notes"); setActiveVideo(null); setCurrentPos(0); }, [chapter.id]);

  const hasTranscript = !!(activeVideo?.transcript || []).length;
  const downloads = chapter.downloads || []; // Optional — chapters may
                                              // expose downloads later.
  const hasResources = downloads.length > 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="chapter-reader"
                className="flex gap-6 -mx-2">
      {/* ─── LEFT SIDEBAR (sticky 260px) ─── */}
      <aside className="w-[260px] flex-shrink-0 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto bg-white rounded-2xl p-4"
             style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
             data-testid="chapter-sidebar">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[11px] font-semibold hover:underline mb-3 text-slate-500"
                data-testid="reader-back-btn">
          <ArrowLeft className="h-3 w-3" /> Back to Learning Paths
        </button>
        <h3 className="text-[13px] font-bold text-[#1B365D]">{course.title}</h3>
        <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500"
               style={{ width: `${progressPct}%`, background: accent }} />
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">
          {completedCount} of {allChapters.length} chapters · {progressPct}%
        </p>
        <ul className="mt-4 space-y-0.5" data-testid="chapter-sidebar-list">
          {allChapters.map((ch, idx) => {
            const isActive = ch.id === chapter.id;
            const isComplete = !!completedChapters?.[ch.id];
            return (
              <li key={ch.id}>
                <button onClick={() => onSelectChapter?.(ch)}
                        className={`w-full text-left px-2 py-2 rounded-lg flex items-start gap-2 transition ${isActive ? "bg-blue-50 border-l-[3px]" : "hover:bg-slate-50 border-l-[3px] border-transparent"}`}
                        style={isActive ? { borderLeftColor: accent } : {}}
                        data-testid={`sidebar-chapter-${ch.id}`}>
                  {/* Completion circle */}
                  <span className="flex-shrink-0 mt-0.5">
                    {isComplete ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : isActive ? (
                      <span className="block h-3.5 w-3.5 rounded-full border-2" style={{ borderColor: accent }} />
                    ) : (
                      <span className="block h-3.5 w-3.5 rounded-full border-2 border-slate-300" />
                    )}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block text-[9px] font-bold uppercase tracking-wide ${isActive ? "" : "text-slate-400"}`}
                          style={isActive ? { color: accent } : {}}>
                      CH {ch.order || idx + 1}
                    </span>
                    <span className={`block text-[12px] font-semibold leading-tight truncate ${isComplete ? "text-slate-400" : isActive ? "text-[#1B365D]" : "text-slate-700"}`}>
                      {ch.title}
                    </span>
                    <span className="block text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" /> {ch.estimated_time || "—"}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* ─── RIGHT COLUMN (chapter content) ─── */}
      <div className="flex-1 min-w-0" data-testid="chapter-main">
        {/* Video player + carousel (top of right column).  P2 custom
            controls bar + chapter-strip multi-video carousel rendered
            inside VideoPlayerStrip.  We surface `activeVideo` and
            `currentPos` up so the Transcript tab can render below. */}
        <VideoPlayerStrip
          courseId={course.id}
          chapterId={chapter.id}
          accent={accent}
          onChapterAutoComplete={onAutoComplete}
          onActiveVideoChange={setActiveVideo}
          onProgressTick={({ pos }) => setCurrentPos(pos)}
        />

        {/* Chapter pill + title meta */}
        <div className="px-1 mb-3">
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5"
                style={{ color: accent }}
                data-testid="chapter-pill">
            CH {chapter.order || currentChIdx + 1} · {chapter.title}
          </span>
          {activeVideo && (
            <div className="text-[11px] text-slate-500 font-mono">
              Now playing: {activeVideo.title} · {activeVideo.duration} · {currentChIdx + 1} of {allChapters.length} chapters
            </div>
          )}
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 border-b border-slate-200 mb-4" data-testid="chapter-tabs">
          <TabButton id="notes" label="Notes" active={tab === "notes"} accent={accent} onClick={() => setTab("notes")} />
          <TabButton id="transcript" label={hasTranscript ? `Transcript (${activeVideo.transcript.length})` : "Transcript"}
                     active={tab === "transcript"} accent={accent} onClick={() => setTab("transcript")}
                     disabled={!hasTranscript} />
          <TabButton id="resources" label={hasResources ? `Resources (${downloads.length})` : "Resources"}
                     active={tab === "resources"} accent={accent} onClick={() => setTab("resources")} />
          {/* Phase 2.3 (Iter 165) — Spark Notes tab */}
          <TabButton id="spark" label="✦ Spark Notes" active={tab === "spark"} accent={accent} onClick={() => setTab("spark")} />
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-2xl p-6 mb-5" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
             data-testid={`chapter-tab-content-${tab}`}>
          {tab === "notes" && (
            <NotesTab chapter={chapter} sections={sections}
                      sectionIndex={sectionIndex} onSectionChange={onSectionChange}
                      onComplete={onComplete} completedSections={completedSections}
                      accent={accent} />
          )}
          {tab === "transcript" && hasTranscript && (
            <>
              <p className="text-[12px] text-slate-500 mb-2">Click any timestamp to jump to that moment in the video.</p>
              <TranscriptTab video={activeVideo} currentPos={currentPos} />
            </>
          )}
          {tab === "transcript" && !hasTranscript && (
            <div className="py-8 text-center text-xs text-slate-400" data-testid="transcript-empty-state">
              No transcript available for this video yet.
            </div>
          )}
          {tab === "resources" && hasResources && (
            <ul className="space-y-2" data-testid="resources-list">
              {downloads.map((d, idx) => (
                <li key={idx} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                  <div className="text-xs">
                    <div className="font-semibold text-[#1B365D]">{d.title}</div>
                    <div className="text-slate-400 text-[10px]">{d.kind || "FILE"}{d.size ? ` · ${d.size}` : ""}</div>
                  </div>
                  <a href={d.url} download className="text-[11px] font-semibold hover:underline" style={{ color: accent }}>
                    Download
                  </a>
                </li>
              ))}
            </ul>
          )}
          {tab === "resources" && !hasResources && (
            <div className="py-8 text-center text-xs text-slate-400" data-testid="resources-empty-state">
              No downloadable resources for this chapter yet.
            </div>
          )}
          {tab === "spark" && (
            <SparkNotesTab courseId={course.id} chapterId={chapter.id} accent={accent} />
          )}
        </div>

        {/* Footer nav — Previous · Mark complete · Next */}
        <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3 mb-2"
             style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
             data-testid="chapter-footer-nav">
          <Button variant="ghost" disabled={!prevChapter} onClick={() => prevChapter && onSelectChapter?.(prevChapter)}
                  className="text-slate-500 rounded-xl" data-testid="footer-prev-chapter">
            <ChevronLeft className="h-4 w-4 mr-1" /> {prevChapter ? `Previous · ${prevChapter.title.slice(0, 24)}` : "Previous"}
          </Button>
          {!isChapterComplete ? (
            <Button onClick={() => onComplete?.(`${chapter.id}-mark`)} className="rounded-xl text-white"
                    style={{ background: accent }} data-testid="footer-mark-complete">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Mark chapter complete
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
              <CheckCircle2 className="h-4 w-4" /> Chapter complete
            </span>
          )}
          <Button disabled={!nextChapter} onClick={() => nextChapter && onSelectChapter?.(nextChapter)}
                  className="rounded-xl text-white disabled:bg-slate-200 disabled:text-slate-400"
                  style={nextChapter ? { background: accent } : undefined}
                  data-testid="footer-next-chapter">
            {nextChapter ? `Next · ${nextChapter.title.slice(0, 24)}` : "Last chapter"} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/* Tab button — keeps the strip dead-simple and avoids dragging shadcn
   Tabs into a context that needs imperative tab-switching from callers. */
function TabButton({ id, label, active, accent, onClick, disabled }) {
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            className={`text-[12px] font-semibold px-3 py-2 -mb-px border-b-2 transition ${active ? "" : "border-transparent text-slate-500 hover:text-slate-800"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            style={active ? { borderBottomColor: accent, color: accent } : {}}
            data-testid={`chapter-tab-${id}`}>
      {label}
    </button>
  );
}

/* Phase 2.3 (Iter 165) — Spark Notes tab. Generates a 1-liner +
   2-3 paragraph summary + 4-7 key points + 2-4 watch-outs for the
   active chapter via Azure GPT-5 mini. Server-cached 24h, so the
   panel paints instantly on subsequent opens of the same chapter. */
function SparkNotesTab({ courseId, chapterId, accent }) {
  const { token } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null, regenerating: false });

  const load = useCallback(async (force = false) => {
    if (!courseId || !chapterId) return;
    setState((p) => ({ ...p, loading: !force, regenerating: force, error: null }));
    try {
      const { data } = await axios.post(
        `${API}/learning-videos/chapters/${courseId}/${chapterId}/spark-notes`,
        force ? { force: true } : {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 35000 },
      );
      setState({ loading: false, data, error: null, regenerating: false });
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message,
        regenerating: false });
    }
  }, [courseId, chapterId, token]);

  useEffect(() => { load(false); }, [load]);

  if (state.loading) {
    return (
      <div data-testid="spark-notes-loading" className="flex items-center gap-2 py-8 justify-center text-slate-400 text-xs">
        <Loader2 className="h-4 w-4 animate-spin" /> Azure GPT-5 mini is reading the chapter…
      </div>
    );
  }
  if (state.error) {
    return (
      <div data-testid="spark-notes-error" className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">Couldn't generate Spark Notes.</div>
          <div>{String(state.error)}</div>
        </div>
      </div>
    );
  }
  const d = state.data || {};
  return (
    <div data-testid="spark-notes-tab" className="flex flex-col gap-4 text-[13px] text-slate-700">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" style={{ color: accent }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.08em]" style={{ color: accent }}>Spark Notes</span>
          {d.cached && (
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">cached</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={state.regenerating}
          data-testid="spark-notes-regenerate"
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800"
        >
          {state.regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Regenerate
        </button>
      </div>

      {d.one_liner && (
        <div className="text-[14px] font-semibold text-[#0f172a] leading-relaxed">{d.one_liner}</div>
      )}
      {d.summary && (
        <div className="whitespace-pre-line text-[12.5px] text-slate-700 leading-relaxed">{d.summary}</div>
      )}

      {Array.isArray(d.key_points) && d.key_points.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-2">Key points</div>
          <ul className="space-y-1.5 list-disc pl-5">
            {d.key_points.map((kp, i) => (
              <li key={i} data-testid={`spark-key-point-${i}`} className="text-[12px] text-slate-700">{kp}</li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(d.watch_outs) && d.watch_outs.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-amber-600 mb-2">Watch-outs</div>
          <ul className="space-y-1.5">
            {d.watch_outs.map((w, i) => (
              <li key={i} data-testid={`spark-watch-out-${i}`} className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[12px] text-amber-900 flex gap-2">
                <AlertTriangle className="h-3 w-3 mt-1 flex-shrink-0" /> {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[10px] text-slate-400 mt-1">
        Generated by Azure GPT-5 mini. Cached 24 hours per chapter — fresh edits to the source notes auto-invalidate.
      </div>
    </div>
  );
}

/* Notes tab — renders all sections (or, if multiple sections, a
   sub-stepper within the Notes view so progress / mark-complete still
   works per section). */
function NotesTab({ chapter, sections, sectionIndex, onSectionChange, onComplete, completedSections, accent }) {
  if (!sections.length) {
    return <div className="text-center py-8 text-xs text-slate-400">No notes content for this chapter yet.</div>;
  }
  const currentSection = sections[sectionIndex];
  const sectionKey = `${chapter.id}-${sectionIndex}`;
  const isSectionComplete = completedSections?.[sectionKey];
  const isMulti = sections.length > 1;

  return (
    <>
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
        <h3 className="text-base font-bold text-[#1B365D]">{currentSection.title}</h3>
        {isMulti && (
          <span className="text-[10px] font-semibold text-slate-400">{sectionIndex + 1} / {sections.length}</span>
        )}
      </div>
      <div className="prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {currentSection.content}
        </ReactMarkdown>
      </div>

      {isMulti && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
          <Button variant="ghost" disabled={sectionIndex === 0}
                  onClick={() => onSectionChange(sectionIndex - 1)}
                  className="text-slate-500 rounded-xl" data-testid="prev-section-btn">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous section
          </Button>
          <div className="flex gap-2">
            {!isSectionComplete && (
              <Button variant="ghost" onClick={() => onComplete?.(sectionKey)}
                      className="rounded-xl text-xs" style={{ color: accent }}
                      data-testid="mark-section-complete">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark section
              </Button>
            )}
            <Button disabled={sectionIndex === sections.length - 1}
                    onClick={() => onSectionChange(sectionIndex + 1)}
                    className="rounded-xl text-white"
                    style={sectionIndex < sections.length - 1 ? { background: accent } : undefined}
                    data-testid="next-section-btn">
              Next section <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Course View ─── */
function CourseView({ course, onBack, onOpenChapter, completedChapters }) {
  const totalChapters = course.chapters?.length || 0;
  const completedCount = course.chapters?.filter((ch) => completedChapters[ch.id]).length || 0;
  const progressPct = totalChapters > 0 ? Math.round((completedCount / totalChapters) * 100) : 0;

  return (
    <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} data-testid={`course-view-${course.id}`}>
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold hover:underline mb-4" style={{ color: COURSE_ACCENT[course.color] }} data-testid="back-to-courses">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Learning Paths
      </button>

      {/* Course Header */}
      <div className="rounded-2xl p-6 mb-5 text-white" style={{ background: COURSE_GRADIENT[course.color] || COURSE_GRADIENT.blue }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              {(() => { const Icon = ICON_MAP[course.icon] || BookOpen; return <Icon className="h-6 w-6 text-white" />; })()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{course.title}</h2>
              <p className="text-[13px] text-white/80 mt-1 max-w-lg">{course.description}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-extrabold text-white">{progressPct}%</div>
            <div className="text-xs text-white/70">{completedCount}/{totalChapters} chapters</div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Chapters List */}
      <div className="space-y-1.5">
        {course.chapters?.map((ch, idx) => {
          const accent = CHAPTER_ACCENTS[idx % CHAPTER_ACCENTS.length];
          const Icon = ICON_MAP[ch.icon] || BookOpen;
          const isComplete = completedChapters[ch.id];
          return (
            <motion.button
              key={ch.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              onClick={() => onOpenChapter(ch)}
              className="w-full text-left rounded-xl bg-white transition-all duration-200 hover:shadow-md hover:-translate-y-px flex overflow-hidden"
              style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
              data-testid={`chapter-card-${ch.id}`}
            >
              {/* Color accent bar */}
              <div className="w-1 flex-shrink-0 rounded-l-xl" style={{ background: isComplete ? "#10B981" : accent }} />
              <div className="flex items-center gap-3 py-3 px-4 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: isComplete ? "#ECFDF5" : `${accent}12` }}>
                  {isComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Icon className="h-4 w-4" style={{ color: accent }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Ch {ch.order || idx + 1}</span>
                    {isComplete && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Done</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-[#1B365D] truncate">{ch.title}</h3>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-slate-400">
                  <span className="text-[10px] flex items-center gap-1"><Clock className="h-3 w-3" /> {ch.estimated_time}</span>
                  <span className="text-[10px]">{ch.sections?.length || 0} sec</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ─── Course Library ─── */
function CourseLibrary({ courses, onSelectCourse, completedChapters, searchQuery, setSearchQuery }) {
  const [filter, setFilter] = useState("all");

  const getCourseProgress = (course) => {
    const total = course.chapters?.length || 0;
    const done = course.chapters?.filter((ch) => completedChapters[ch.id]).length || 0;
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const globalStats = useMemo(() => {
    let total = 0, done = 0;
    courses.forEach((c) => {
      const p = getCourseProgress(c);
      total += p.total;
      done += p.done;
    });
    return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCourseProgress is a stable reducer over (courses, completedChapters); re-running only when those change
  }, [courses, completedChapters]);

  const filteredCourses = useMemo(() => {
    let list = courses;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.chapters?.some((ch) => ch.title.toLowerCase().includes(q) || ch.subtitle?.toLowerCase().includes(q))
      );
    }
    if (filter === "completed") list = list.filter((c) => getCourseProgress(c).pct === 100);
    if (filter === "in-progress") list = list.filter((c) => { const p = getCourseProgress(c); return p.done > 0 && p.pct < 100; });
    if (filter === "not-started") list = list.filter((c) => getCourseProgress(c).done === 0);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCourseProgress reads only from `courses`+`completedChapters` already in deps
  }, [courses, searchQuery, filter, completedChapters]);

  const FILTERS = [
    { id: "all", label: "All Courses" },
    { id: "not-started", label: "Not Started" },
    { id: "in-progress", label: "In Progress" },
    { id: "completed", label: "Completed" },
  ];

  return (
    <div data-testid="learning-paths">
      {/* Global Progress Banner */}
      <div className="rounded-2xl p-5 mb-6 text-white" style={{ background: "linear-gradient(135deg, #0A1628 0%, #1B365D 50%, #0071E3 100%)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Your Learning Progress</h3>
              <p className="text-[12px] text-white/70">{globalStats.done} of {globalStats.total} chapters completed across {courses.length} courses</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold text-white">{globalStats.pct}%</div>
          </div>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${globalStats.pct}%` }} />
        </div>
      </div>

      {/* Search + Filter Row */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search courses, chapters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl border-slate-200"
            data-testid="learning-search"
          />
        </div>
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                filter === f.id
                  ? "bg-[#1B365D] text-white shadow-sm"
                  : "bg-white text-slate-500 hover:bg-slate-50"
              }`}
              style={filter !== f.id ? { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" } : {}}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Course Cards */}
      <div className="space-y-3">
        {filteredCourses.map((course, idx) => {
          const Icon = ICON_MAP[course.icon] || BookOpen;
          const accent = COURSE_ACCENT[course.color] || COURSE_ACCENT.blue;
          const { total, done, pct } = getCourseProgress(course);
          const totalTime = course.chapters?.reduce((sum, ch) => sum + parseInt(ch.estimated_time) || 0, 0) || 0;

          return (
            <motion.button
              key={course.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => onSelectCourse(course)}
              className="w-full text-left rounded-2xl bg-white transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 flex overflow-hidden"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
              data-testid={`course-card-${course.id}`}
            >
              {/* Color accent bar */}
              <div className="w-1.5 flex-shrink-0" style={{ background: accent }} />
              <div className="flex items-center gap-4 p-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}14` }}>
                  <Icon className="h-6 w-6" style={{ color: accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-[#1B365D] truncate">{course.title}</h3>
                  <p className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">{course.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[180px]">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct === 100 ? "#10B981" : accent }} />
                    </div>
                    <span className="text-[10px] font-semibold" style={{ color: pct === 100 ? "#10B981" : accent }}>{pct}%</span>
                    <span className="text-[10px] text-slate-400">{done}/{total} chapters</span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{totalTime} min</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] font-semibold" style={{ color: accent }}>
                    {pct === 100 ? "Review" : done > 0 ? "Continue" : "Start"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {filteredCourses.length === 0 && (
        <div className="text-center py-16">
          <Search className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{searchQuery ? `No results for "${searchQuery}"` : "No courses match this filter"}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
export default function LearningPaths({ initialCourseId = null, initialChapterId = null } = {}) {
  const { token } = useAuth();
  const canManageVideos = useCan("videos_admin");
  const [view, setView] = useState("learner"); // 'learner' | 'manage'
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const [completedChapters, setCompletedChapters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp_completed_chapters") || "{}"); } catch { return {}; }
  });
  const [completedSections, setCompletedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lp_completed_sections") || "{}"); } catch { return {}; }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCourses defined below; run once on mount
  useEffect(() => { fetchCourses(); }, []);

  // P1 — Silent one-time localStorage→server migration of chapter
  // completion (Josh decision #10).  After hydrating from /progress/me,
  // we POST any localStorage-only entries to the server, then mark them
  // as migrated so we don't loop. Failures are non-fatal — the UI
  // continues to read the localStorage map until the next successful
  // hydrate.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/learning-videos/progress/me`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        const serverMap = res.data?.by_chapter || {};
        // Merge server → local (server is source of truth going forward)
        const localRaw = JSON.parse(localStorage.getItem("lp_completed_chapters") || "{}");
        const merged = { ...localRaw, ...serverMap };
        setCompletedChapters(merged);
        localStorage.setItem("lp_completed_chapters", JSON.stringify(merged));
        // Push localStorage-only entries to the server (silent migration)
        const migratedFlag = localStorage.getItem("lp_progress_migrated_v1") === "1";
        if (!migratedFlag) {
          const localOnly = Object.keys(localRaw).filter((k) => !serverMap[k]);
          // Resolve chapter→course by walking the (already-loaded) courses
          for (const chId of localOnly) {
            const owningCourse = (courses || []).find((c) => (c.chapters || []).some((ch) => ch.id === chId));
            if (!owningCourse) continue;
            try {
              await axios.post(`${API}/learning-videos/progress/${owningCourse.id}/${chId}/complete`, {},
                { headers: { Authorization: `Bearer ${token}` } });
            } catch { /* non-fatal */ }
          }
          localStorage.setItem("lp_progress_migrated_v1", "1");
        }
      } catch { /* hydrate failure → fall back to localStorage shape */ }
    })();
    return () => { cancelled = true; };
  }, [token, courses]);

  // Deep-link support: once courses are loaded, if a course/chapter id was
  // passed in via props (typically from the Sales Dialer → Resources
  // navigation), pre-select them.
  useEffect(() => {
    if (!courses.length) return;
    if (initialCourseId) {
      const course = courses.find((c) => c.id === initialCourseId);
      if (course) {
        setSelectedCourse(course);
        if (initialChapterId) {
          const chapter = (course.chapters || []).find((ch) => ch.id === initialChapterId);
          if (chapter) { setActiveChapter(chapter); setSectionIndex(0); }
        }
      }
    }
    // Only run once courses first become available. Subsequent in-component
    // navigation (back buttons, other clicks) owns the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/learning-paths`, { headers: { Authorization: `Bearer ${token}` } });
      setCourses(res.data.courses || []);
    } catch { toast.error("Failed to load learning paths"); }
    finally { setLoading(false); }
  };

  const markSectionComplete = useCallback((sectionKey) => {
    const next = { ...completedSections, [sectionKey]: true };
    setCompletedSections(next);
    localStorage.setItem("lp_completed_sections", JSON.stringify(next));
    if (activeChapter) {
      const allDone = activeChapter.sections.every((_, i) => next[`${activeChapter.id}-${i}`]);
      if (allDone) {
        const chNext = { ...completedChapters, [activeChapter.id]: true };
        setCompletedChapters(chNext);
        localStorage.setItem("lp_completed_chapters", JSON.stringify(chNext));
        toast.success("Chapter completed!");
        // P1 — Persist completion server-side (best-effort)
        if (selectedCourse?.id) {
          axios.post(`${API}/learning-videos/progress/${selectedCourse.id}/${activeChapter.id}/complete`, {},
            { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        }
      }
    }
  }, [completedSections, completedChapters, activeChapter, selectedCourse, token]);

  // P3 — Auto-mark-complete callback fired when learner crosses ≥90%
  // watch threshold on the active video.  Updates local state + persists
  // to the server (best-effort).  Declared BEFORE any early return so it
  // satisfies the react-hooks/rules-of-hooks order invariant.
  const autoCompleteChapter = useCallback(() => {
    if (!activeChapter || !selectedCourse) return;
    if (completedChapters[activeChapter.id]) return;
    const chNext = { ...completedChapters, [activeChapter.id]: true };
    setCompletedChapters(chNext);
    localStorage.setItem("lp_completed_chapters", JSON.stringify(chNext));
    toast.success(`Chapter auto-completed — you've watched 90%+ of "${activeChapter.title}"`);
    axios.post(`${API}/learning-videos/progress/${selectedCourse.id}/${activeChapter.id}/complete`, {},
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [activeChapter, selectedCourse, completedChapters, token]);

  const markChapterComplete = useCallback((chId) => {
    if (!chId || !selectedCourse) return;
    if (completedChapters[chId]) return;
    const chNext = { ...completedChapters, [chId]: true };
    setCompletedChapters(chNext);
    localStorage.setItem("lp_completed_chapters", JSON.stringify(chNext));
    toast.success("Chapter complete!");
    axios.post(`${API}/learning-videos/progress/${selectedCourse.id}/${chId}/complete`, {},
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [selectedCourse, completedChapters, token]);

  // Combined onComplete — handles BOTH section-level marks (existing
  // sub-step flow) AND chapter-level marks (footer button).  The footer
  // button passes `${chapterId}-mark` which we route to chapter-level.
  const handleComplete = useCallback((key) => {
    if (key && key.endsWith("-mark") && activeChapter) {
      markChapterComplete(activeChapter.id); return;
    }
    markSectionComplete(key);
  }, [activeChapter, markChapterComplete, markSectionComplete]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#0071E3]" />
        <span className="ml-2 text-sm text-slate-500">Loading courses...</span>
      </div>
    );
  }

  // Admin mode toggle — render the Manage Videos panel.  The learner
  // UI is unchanged; admins flip the toggle to curate the library.
  const adminToggle = canManageVideos ? (
    <div className="flex items-center justify-end gap-2 mb-4" data-testid="learning-paths-mode-toggle">
      <button onClick={() => setView("learner")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${view === "learner" ? "bg-[#0071E3] text-white" : "bg-slate-100 text-slate-600"}`}
              data-testid="lp-view-learner">Learner</button>
      <button onClick={() => setView("manage")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 ${view === "manage" ? "bg-[#0071E3] text-white" : "bg-slate-100 text-slate-600"}`}
              data-testid="lp-view-manage">
        <Video className="h-3.5 w-3.5" /> Manage Videos
      </button>
    </div>
  ) : null;

  if (canManageVideos && view === "manage") {
    return (
      <>
        {adminToggle}
        <VideoLibraryPanel courses={courses} />
      </>
    );
  }

  if (activeChapter && selectedCourse) {
    return (
      <ChapterReader
        chapter={activeChapter}
        course={selectedCourse}
        sectionIndex={sectionIndex}
        onSectionChange={setSectionIndex}
        onBack={() => setActiveChapter(null)}
        onComplete={handleComplete}
        onAutoComplete={autoCompleteChapter}
        completedSections={completedSections}
        completedChapters={completedChapters}
        onSelectChapter={(ch) => { setActiveChapter(ch); setSectionIndex(0); }}
      />
    );
  }

  if (selectedCourse) {
    // Iter 347 — gated video course (CCM Sales Certification): checkpoint-
    // gated player with in-video knowledge checks + certification. Any
    // course flagged `gated` (or carrying checkpoint questions) uses it.
    if (selectedCourse.gated) {
      return (
        <GatedCourseView
          course={selectedCourse}
          onBack={() => setSelectedCourse(null)}
        />
      );
    }
    // Apple Liquid Glass UI for EMR Sales Toolkit only (Danny — May 2026).
    // Every other course keeps the original CourseView. Branching here
    // instead of inside CourseView so the EMR variant can iterate on
    // typography/motion/spacing independently.
    if (selectedCourse.id === "emr-sales-toolkit") {
      return (
        <LiquidGlassEMRCourseView
          course={selectedCourse}
          onBack={() => setSelectedCourse(null)}
          onOpenChapter={(ch) => { setActiveChapter(ch); setSectionIndex(0); }}
          completedChapters={completedChapters}
        />
      );
    }
    return (
      <CourseView
        course={selectedCourse}
        onBack={() => setSelectedCourse(null)}
        onOpenChapter={(ch) => { setActiveChapter(ch); setSectionIndex(0); }}
        completedChapters={completedChapters}
      />
    );
  }

  return (
    <>
      {adminToggle}
      <CourseLibrary
        courses={courses}
        onSelectCourse={setSelectedCourse}
        completedChapters={completedChapters}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    </>
  );
}
